import {
  MINIMIZED_CALL_BANNER_DOCK_ELEMENT_ID,
  MINIMIZED_CALL_DOCK_ACTIVE_ATTR,
  MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR,
  MINIMIZED_CALL_DOCK_RESERVE_FALLBACK_PX,
} from '@act/toolkit/styles/callMinimize.constants';
import { useEffect } from 'react';

/**
 * Toggles the document attribute that signals minimized-call dock layout offsets.
 *
 * @param active - When true, mark the dock as visible on {@link document.documentElement}.
 */
function setMinimizedCallDockDocumentActive(active: boolean): void {
  const root = document.documentElement;
  if (active) {
    root.setAttribute(MINIMIZED_CALL_DOCK_ACTIVE_ATTR, 'true');
  } else {
    root.removeAttribute(MINIMIZED_CALL_DOCK_ACTIVE_ATTR);
  }
}

/**
 * Publishes the minimized call dock height as a document-level CSS variable so
 * the app shell can reserve bottom space and avoid covering page content.
 *
 * @param active - When true, observe dock height and set the CSS variable.
 */
export function useMinimizedCallDockReserveHeight(active: boolean): void {
  useEffect(() => {
    const root = document.documentElement;

    if (!active) {
      root.style.removeProperty(MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR);
      setMinimizedCallDockDocumentActive(false);
      return;
    }

    setMinimizedCallDockDocumentActive(true);

    const dockElement = document.getElementById(
      MINIMIZED_CALL_BANNER_DOCK_ELEMENT_ID
    );
    if (!dockElement) {
      root.style.setProperty(
        MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR,
        `${MINIMIZED_CALL_DOCK_RESERVE_FALLBACK_PX}px`
      );
      return () => {
        root.style.removeProperty(MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR);
        setMinimizedCallDockDocumentActive(false);
      };
    }

    const publishHeight = () => {
      root.style.setProperty(
        MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR,
        `${dockElement.offsetHeight}px`
      );
    };

    publishHeight();

    const observer = new ResizeObserver(publishHeight);
    observer.observe(dockElement);

    return () => {
      observer.disconnect();
      root.style.removeProperty(MINIMIZED_CALL_DOCK_RESERVE_CSS_VAR);
      setMinimizedCallDockDocumentActive(false);
    };
  }, [active]);
}
